import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import _ from 'lodash';
import { generatePath } from 'react-router-dom';

import notification from 'app/config/notification';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { errorHandler } from 'app/utils/errorHandler';

import {
    clearDuplicatedRegistry,
    clearLineScheduleBoard,
    clearSchedulesStore,
    clearSituations,
    fetchLine,
    fetchSituations,
    setLineScheduleBoard,
    setSchedulesStore,
} from 'app/store/slices/line';

import { Button, CircularProgress, makeStyles, TextField } from '@material-ui/core';

import Grid from '@material-ui/core/Grid';

import Loading from 'app/layouts/Loading';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import ScheduleTableBoard from 'app/components/ScheduleTableBoard/ScheduleTableBoard';
import PermissionariesTable from '../../../components/ServicesForms/Lines/components/PermissionariesTable';
import RequestProcess from '../components/RequestProcess';
import ScheduleModal from './modals/ScheduleModal';

import { scheduleValidationSchema } from '../form-configs/validation-schemas';
import { LINES_SITUATIONS, SCHEDULES_TYPES, SERVICES_CODES } from 'app/config/services';
import { scheduleInitialValues } from '../form-configs/schedule-initial-values';
import { URLS } from '../../../config/urls';
import { validateFormikForm } from '../../../utils/forms/functions';
import { TIMEZONE_OFFSET } from '../../../utils/constants';

import styles from './styles';
import InfoLineCompany from '../components/InfoLineCompany';
import moment from 'moment';
import uuidUtil from 'app/utils/uuid';
import { FormServiceContext } from '../../../utils/contexts';

const SchedulesDetails = (props) => {
    const history = useHistory();
    const match = useRouteMatch();
    const classes = useStyles();
    const dispatch = useDispatch();

    const cdLinhaFromPath = React.useRef(match.params.cdLinha).current;
    const cdScheduleFromPath = React.useRef(match.params.cdQuadroHorario).current;

    const situationsStore = useSelector((state) => state.line.situations);
    const selectedLineStore = useSelector((state) => state.line.selected);
    const duplicatedSchedule = useSelector((state) => state.line.duplicatedRegistry);
    const schedulesStore = useSelector((state) => state.line.schedulesStore);
    const scheduleBoard = useSelector((state) => state.line.lineScheduleBoard);
    const isStrip = useSelector((state) => state.line.isStrip);
    const empresa = useSelector((state) => state.line.selected?.empresa);

    const [servicesFilter, setServicesFilter] = useState([
        { cdServico: SERVICES_CODES.SLIC_AMPLIACAO_HORARIOS },
        { cdServico: SERVICES_CODES.SLIC_MODIFICACAO_HORARIOS },
        { cdServico: SERVICES_CODES.SLIC_SUPRESSAO_HORARIOS },
        { cdServico: SERVICES_CODES.SLIC_TRANSFERENCIA_PERMISSAO },
        { cdServico: SERVICES_CODES.SLIC_CANCELAMENTO_PERMISSAO },
    ]);

    const [loading, setLoading] = useState(true);
    const [loadingServiceFilter, setLoadingServiceFilter] = useState(true);
    const [loadingSchedules, setLoadingSchedules] = useState(false);

    const [permissionaries, setPermissionaries] = useState([]);
    const [lineToCopy, setLineToCopy] = useState(null);
    const [allLinesToCopy, setAllLinesToCopy] = useState([]);

    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState({});
    const [requestProcessValues, setRequestProcessValues] = useState({});
    const [changedPermissionaries, setChangedPermissionaries] = useState(false);
    const [scheduleTypes, setScheduleTypes] = useState([]);
    const [selectedScheduleTypes, setSelectedScheduleTypes] = useState(0);

    const [formInitials, setFormInitials] = useState(scheduleInitialValues);

    const canEdit = useMemo(() => {
        return !(
            scheduleBoard &&
            scheduleBoard.cdLinhaSituacao &&
            scheduleBoard.cdLinhaSituacao !== LINES_SITUATIONS.EM_REVISAO.code
        );
    }, [scheduleBoard]);

    const canGenerateScheduleBoard = useMemo(() => {
        return (
            scheduleBoard &&
            scheduleBoard.cdLinhaSituacao &&
            (scheduleBoard.cdLinhaSituacao === LINES_SITUATIONS.EM_REVISAO.code ||
                scheduleBoard.cdLinhaSituacao === LINES_SITUATIONS.VIGENTE.code)
        );
    }, [scheduleBoard]);

    useEffect(() => {
        (async () => {
            setLoadingServiceFilter(true);
            if (isStrip) {
                setServicesFilter([
                    { cdServico: SERVICES_CODES.STRIP_PROLONGAMENTO_LINHA },
                    { cdServico: SERVICES_CODES.STRIP_ENCURTAMENTO_LINHA },
                    { cdServico: SERVICES_CODES.STRIP_REFORCO_HORARIOS },
                    { cdServico: SERVICES_CODES.STRIP_CONEXAO_LINHAS },
                    { cdServico: SERVICES_CODES.STRIP_ALTERACAO_DEFINITIVA_ITINERARIO },
                    { cdServico: SERVICES_CODES.STRIP_ALTERACAO_PROVISORIA_ITINERARIO },
                    { cdServico: SERVICES_CODES.STRIP_INCLUSAO_TIPO_EQUIPAMENTO },
                    { cdServico: SERVICES_CODES.STRIP_SUBSTITUICAO_TIPO_EQUIPAMENTO },
                    { cdServico: SERVICES_CODES.STRIP_IMPLANTACAO_SECAO },
                    { cdServico: SERVICES_CODES.STRIP_SUPRESSAO_SECAO },
                    { cdServico: SERVICES_CODES.STRIP_CANCELAMENTO_LINHA },
                    { cdServico: SERVICES_CODES.STRIP_CANCELAMENTO_SERVICO },
                    { cdServico: SERVICES_CODES.STRIP_MODIFICACAO_HORARIOS },
                    { cdServico: SERVICES_CODES.STRIP_AMPLIACAO_HORARIOS },
                    { cdServico: SERVICES_CODES.STRIP_SUPRESSAO_HORARIOS },
                    { cdServico: SERVICES_CODES.STRIP_PRORROGACAO_PRAZO },
                    { cdServico: SERVICES_CODES.STRIP_TRANSFERENCIA_LINHA },
                ]);

                const responseLinesToCopy = await lineEndpoint.getLineWithSchedules();
                if (responseLinesToCopy && responseLinesToCopy.status === 200) {
                    setAllLinesToCopy(responseLinesToCopy.data || []);
                }
            }
            setLoadingServiceFilter(false);
        })();
    }, [isStrip]);

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                const promisses = [];
                if (situationsStore.length === 0) promisses.push(dispatch(fetchSituations()));
                if (!selectedLineStore && cdLinhaFromPath && cdLinhaFromPath)
                    promisses.push(dispatch(fetchLine(cdLinhaFromPath)));
                if (promisses.length > 0) await Promise.all(promisses);
                if (!isStrip && (!permissionaries || permissionaries.length === 0)) {
                    const responsePermissionary = await lineEndpoint.getLinePermissionaries(cdLinhaFromPath);
                    if (responsePermissionary && responsePermissionary.status === 200 && responsePermissionary.data) {
                        setPermissionaries(responsePermissionary.data);
                    }
                }
                if (scheduleTypes.length === 0) {
                    const response = await lineEndpoint.getScheduleTypes();
                    if (response && response.status === 200) {
                        setScheduleTypes(response.data);
                    }
                }
                setLoading(false);
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            dispatch(clearSituations());
            dispatch(clearLineScheduleBoard());
            dispatch(clearSchedulesStore());
            dispatch(clearDuplicatedRegistry());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            if (duplicatedSchedule && Object.keys(duplicatedSchedule).length > 0) {
                const schedule = _.cloneDeep(duplicatedSchedule);
                if (schedule.cdTipoQuadroHorario && schedule.cdTipoQuadroHorario !== 0) {
                    setSelectedScheduleTypes(schedule.cdTipoQuadroHorario);
                }
                schedule.cdRevisao = selectedLineStore?.qtdQuadrosHorarios
                    ? selectedLineStore.qtdQuadrosHorarios + 1
                    : '1';
                schedule.cdLinhaSituacao = LINES_SITUATIONS.EM_REVISAO.code;
                schedule.boAssinado = false;
                schedule.cdLinhaQuadroHorario = '';
                schedule.nuApostilaFmt = '';
                schedule.nuApostila = '';
                schedule.idFile = '';
                schedule.cdUsuarioResponsavel = '';
                schedule.deUsuario = '';
                schedule.dtCadastro = new Date();
                schedule.dtVigencia = null;
                schedule.cdSolicitacao = '';
                schedule.nuSolicitacao = '';
                schedule.nuProcesso = '';
                schedule.cdProcesso = '';
                if (schedule.horarios) {
                    const tempSchedule = schedule.horarios.map((entry) => {
                        const temp = { ...entry };
                        const tempId = uuidUtil.generate();
                        temp.tempId = tempId;
                        temp.cdLinhaQuadroHorarioHora = tempId;
                        delete temp.cdLinhaQuadroHorario;
                        if (temp.permissionarios) {
                            const tempPermissionarios = temp.permissionarios.map((entry) => {
                                const tempPerm = { ...entry };
                                tempPerm.tempId = tempId;
                                tempPerm.cdLinhaQuadroHoraResponsavel = null;
                                tempPerm.cdLinhaQuadroHorarioHora = null;
                                return tempPerm;
                            });
                            temp.permissionarios = tempPermissionarios;
                        }
                        return temp;
                    });
                    schedule.horarios = tempSchedule;
                    dispatch(setSchedulesStore(tempSchedule));
                }
                dispatch(setLineScheduleBoard(schedule));
                setFormInitials({ ...scheduleInitialValues, ...schedule });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duplicatedSchedule, selectedLineStore]);

    useEffect(() => {
        (async () => {
            try {
                if (cdScheduleFromPath) {
                    setLoadingSchedules(true);
                    if (!scheduleBoard) {
                        const response = await lineEndpoint.getSchedule(cdScheduleFromPath);
                        if (response && response.status === 200) {
                            const schedule = response.data;
                            if (!isStrip && schedule.permissionarios) {
                                setPermissionaries(schedule.permissionarios);
                            }
                            if (schedule.horarios) {
                                dispatch(setSchedulesStore(schedule.horarios));
                            }
                            setSelectedScheduleTypes(schedule.cdTipoQuadroHorario);
                            setFormInitials({ ...scheduleInitialValues, ...schedule });
                            if (schedule.solicitacao) {
                                schedule.nuSolicitacaoFmt = schedule.solicitacao.nuSolicitacaoFmt;
                            }
                            delete schedule.horarios;
                            dispatch(setLineScheduleBoard(schedule));
                        }
                    }
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            } finally {
                setLoadingSchedules(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdScheduleFromPath]);

    const handleGenerateQH = async ({ values, formProps }) => {
        const errors = validateDocumentGeneration();
        if (errors.length > 0) {
            errors.forEach((e, i) => {
                notification(e, { toastId: `VALIDATION${i}`, type: 'error' });
            });
            return;
        }
        const validation = await validateFormikForm(formProps, scheduleValidationSchema);
        if (Object.keys(validation).length > 0) {
            notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                toastId: 'VALIDATION',
                type: 'error',
            });
            return;
        }
        // else {
        //   await handleSubmitForm(values, formProps, true);
        // }
        setModalData({
            line: selectedLineStore,
            schedule: values,
            requestProcessValues: requestProcessValues,
        });
        setOpenModal(true);
    };

    const updateSchedule = (data) => {
        dispatch(setLineScheduleBoard(data));
        setFormInitials({ ...scheduleInitialValues, ...data });
        if (data.horarios) {
            dispatch(setSchedulesStore(data.horarios));
        }
    };

    const validateDocumentGeneration = () => {
        const errors = [];
        if (!isStrip) {
            const authorized = permissionaries.filter((p) => p.boAutorizado);
            if (authorized.length === 0) {
                errors.push('É necessário pelo menos um permissionário autorizado.');
            }
            if (!selectedLineStore.hasTarifaVigente) {
                errors.push(
                    `É necessário o cadastro de pelo menos uma tarifa vigente para a linha ${selectedLineStore.nuLinha}.`,
                );
            }
        }
        return errors;
    };

    const fixTimezone = (hour) => {
        const currentOffset = moment().utcOffset();
        const diffInMinutes = currentOffset - TIMEZONE_OFFSET.AMERICA_SAOPAULO;
        return moment(hour).add(diffInMinutes, 'minutes');
    };

    const isNewHour = (cdLinhaQuadroHorarioHora) => {
        return uuidUtil.validate(cdLinhaQuadroHorarioHora);
    };

    const handleSubmitForm = async (values, formikHelpers, fromQHGeneration) => {
        try {
            if (!values.cdProcesso || values.cdProcesso === '') return;
            const validSchedules = schedulesStore.filter((entry) => !entry.boExcluido);
            let errors = 0;
            if (!validSchedules || validSchedules.length === 0) {
                if (
                    !isStrip ||
                    !values.cdTipoQuadroHorario ||
                    String(values.cdTipoQuadroHorario) !== String(SCHEDULES_TYPES.ZERADO)
                ) {
                    notification('É necessário a inclusão de pelo menos um item no quadro de horário', {
                        toastId: 'VALIDATION',
                        type: 'error',
                    });
                    errors++;
                }
            }
            if (
                requestProcessValues.nuProcesso &&
                requestProcessValues.nuProcesso !== '' &&
                requestProcessValues.processError
            ) {
                notification('O número do processo não existe no CPA', {
                    toastId: 'VALIDATION_CPA',
                    type: 'error',
                });
                if (formikHelpers) {
                    formikHelpers.setFieldError('nuProcesso', 'O número do processo não existe no CPA');
                }
                errors++;
            }
            if (!requestProcessValues.cdProcesso || requestProcessValues.cdProcesso === '') {
                notification('Informe o número do processo', {
                    toastId: 'VALIDATION_PROCESS',
                    type: 'error',
                });
                errors++;
            }
            if (errors > 0) return;

            setLoading(true);
            const valuesCopy = _.cloneDeep(values);
            valuesCopy.cdLinha = selectedLineStore.cdLinha;
            valuesCopy.nuProcesso = requestProcessValues.nuProcesso;
            valuesCopy.nuSolicitacao = requestProcessValues.nuSolicitacaoFmt;
            valuesCopy.cdProcesso = requestProcessValues.cdProcesso;
            valuesCopy.cdSolicitacao = requestProcessValues.cdSolicitacao;
            valuesCopy.horarios = schedulesStore.map((entry) => ({
                ...entry,
                hrHorario: entry.isNew ? fixTimezone(entry.hrHorario) : entry.hrHorario,
                cdLinhaQuadroHorarioHora: isNewHour(entry.cdLinhaQuadroHorarioHora)
                    ? null
                    : entry.cdLinhaQuadroHorarioHora,
            }));

            if (!isStrip) {
                valuesCopy.permissionarios = permissionaries;
            }

            if (
                Number(valuesCopy.cdLinhaSituacao) === LINES_SITUATIONS.VIGENTE.code ||
                Number(valuesCopy.cdLinhaSituacao) === LINES_SITUATIONS.INATIVO.code
            ) {
                const keySituation = Object.keys(LINES_SITUATIONS).filter(
                    (key) => LINES_SITUATIONS[key].code === Number(valuesCopy.cdLinhaSituacao),
                );
                if (
                    !window.confirm(
                        `Deseja alterar a situação para ${LINES_SITUATIONS[keySituation].name}? Após essa mudança, não será permitido alterar os dados dessa revisão.`,
                    )
                ) {
                    return;
                }
            }

            const savedSchedule = await lineEndpoint.saveSchedule(valuesCopy);
            if (savedSchedule.status === 200) {
                setChangedPermissionaries(false);
                if (!fromQHGeneration) {
                    notification('Revisão salva com sucesso', { toastId: 'VALIDATION', type: 'success' });
                }
                updateSchedule(savedSchedule.data);
                if (!cdScheduleFromPath) {
                    let params = {
                        cdLinha: cdLinhaFromPath,
                        cdQuadroHorario: savedSchedule.data.cdLinhaQuadroHorario,
                    };
                    if (isStrip) params.isStrip = 'strip';
                    history.push(generatePath(URLS.LINES_MANAGE_SCHEDULES_EDIT, params));
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        let tempLink = URLS.LINES_MANAGE_SCHEDULES;
        if (cdLinhaFromPath) {
            let params = { cdLinha: Number(cdLinhaFromPath) };
            if (isStrip) params.isStrip = 'strip';
            history.push(generatePath(tempLink, params));
        }
    };

    const copySchedules = async (formikBag) => {
        setLoading(true);
        if (lineToCopy && lineToCopy !== '') {
            const response = await lineEndpoint.getSchedules({
                page: 0,
                size: 100,
                request: {
                    cdLinha: lineToCopy,
                    cdLinhaSituacao: LINES_SITUATIONS.VIGENTE.code,
                },
            });
            if (response && response.status === 200) {
                if (response.data.content.length > 0) {
                    let data = response.data.content[0];
                    if (data.horarios) {
                        let scheduleType = SCHEDULES_TYPES.PADRAO;
                        if (data.cdTipoQuadroHorario) {
                            scheduleType = data.cdTipoQuadroHorario;
                        }
                        formikBag.setFieldValue('cdTipoQuadroHorario', scheduleType);
                        setSelectedScheduleTypes(scheduleType);

                        let oldSchedules = schedulesStore.map((time) => {
                            const tempTime = { ...time };
                            tempTime.boExcluido = true;
                            return tempTime;
                        });

                        let newSchedules = data.horarios.map((time) => {
                            const tempTime = { ...time };
                            delete tempTime.cdLinhaQuadroHorarioHora;
                            if (cdScheduleFromPath) tempTime.cdLinhaQuadroHorario = Number(cdScheduleFromPath);
                            else delete tempTime.cdLinhaQuadroHorario;
                            return tempTime;
                        });
                        newSchedules.push(...oldSchedules);
                        dispatch(setSchedulesStore(newSchedules));
                    }
                }
            }
        }
        setLoading(false);
    };

    const handleSchedulesRequest = (times) => {
        const oldSchedules = schedulesStore.map((time) => {
            const temp = { ...time };
            temp.boExcluido = true;
            return temp;
        });

        const newSchedules = [];
        times.forEach((time) => {
            const index = oldSchedules.findIndex(
                (entry) =>
                    entry.deDiaSemana.toUpperCase() === time.deDiaSemana.toUpperCase() &&
                    moment(entry.hrHorario).format('HH:mm') === moment(time.hrHorario).format('HH:mm') &&
                    entry.deSentido.toUpperCase() === time.deSentido.toUpperCase(),
            );

            if (index > -1) {
                oldSchedules[index].boExcluido = time.boExcluido;
                return;
            }

            const indexNew = newSchedules.findIndex(
                (entry) =>
                    entry.deDiaSemana.toUpperCase() === time.deDiaSemana.toUpperCase() &&
                    moment(entry.hrHorario).format('HH:mm') === moment(time.hrHorario).format('HH:mm') &&
                    entry.deSentido.toUpperCase() === time.deSentido.toUpperCase(),
            );

            /* horário pode estar duplicado ou ter isNew = true e boExcluido = true ao mesmo tempo em jsonSolicitacao */
            if (indexNew > -1 || time.boExcluido === true) {
                return;
            }

            const temp = {
                ...time,
                boExcluido: false,
                cdLinhaQuadroHorarioHora: index ? index.cdLinhaQuadroHorarioHora : null,
            };

            /* PopoverMenuTable.js:265 - prepareNewPermissionary > buildNewConcessionaria */
            if (!temp.permissionarios && isStrip) {
                temp.permissionarios = buildNewConcessionaria(temp.tempId, scheduleBoard);
            }

            newSchedules.push(temp);
        });
        dispatch(setSchedulesStore([...oldSchedules, ...newSchedules]));
    };

    function buildNewConcessionaria(tempId, scheduleBoard) {
        return [{
            cdLinhaQuadroHoraResponsavel: scheduleBoard?.cdLinhaQuadroHorario,
            cdLinhaQuadroHorarioHora: tempId,
            cnpj: empresa?.deCnpjMatriz,
            cdEmpresa: empresa?.cdEmpresa,
            deRazaoSocial: empresa?.deRazaoSocial,
            boAutorizado: true,
        }];
    }

    const callBackRequest = async (request, fromClick) => {
        if (request && fromClick) {
            const jsonSolicitacao = JSON.parse(request.jsonSolicitacao);
            let error = !jsonSolicitacao.cdLinha;
            if (!error) {
                error = jsonSolicitacao.cdLinha !== selectedLineStore.cdLinha;
                if (error && selectedLineStore.cdLinhaPai !== null) {
                    error = jsonSolicitacao.cdLinha !== selectedLineStore.cdLinhaPai;
                }
                if (error && jsonSolicitacao?.linhasSolicitacao && jsonSolicitacao?.linhasSolicitacao.length > 0) {
                    error = !jsonSolicitacao.linhasSolicitacao.some(
                        (linha) => linha.cdLinha === selectedLineStore.cdLinha,
                    );
                }
            }

            if (error) {
                notification('Essa solicitação não está relacionada com esta linha', {
                    toastId: 'ERROR_REQUEST',
                    type: 'error',
                });
                setRequestProcessValues({});
                return;
            }
            if (
                jsonSolicitacao.quadroHorarios &&
                jsonSolicitacao.quadroHorarios.horarios &&
                jsonSolicitacao.quadroHorarios.horarios.length > 0
            ) {
                if (!isStrip) {
                    handleSchedulesRequest(jsonSolicitacao.quadroHorarios.horarios);
                } else if (window.confirm('Deseja carregar o quadro de horário da solicitação?')) {
                    handleSchedulesRequest(jsonSolicitacao.quadroHorarios.horarios);
                }
            }
        }
    };

    const contextProvider = useMemo(() => {
        return {
            lineAdministration: true,
        };
    }, []);

    return (
        <FormServiceContext.Provider value={contextProvider}>
            <Formik
                enableReinitialize
                initialValues={formInitials}
                initialStatus={{ holding: false }}
                validationSchema={scheduleValidationSchema}
                onSubmit={handleSubmitForm}
                validateOnBlur={false}
                validateOnChange={false}>
                {(formProps) => {
                    const { handleSubmit, handleChange, values } = formProps;
                    return (
                        <Form>
                            <PageWrapper title="Cadastro / Edição de Quadro de Horários">
                                {loading && <Loading />}
                                <Grid container spacing={3}>
                                    <Grid item container spacing={3} direction={'row'}>
                                        <InfoLineCompany selectedLine={selectedLineStore} />
                                    </Grid>

                                    <Grid item xs={12} sm={8} container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Field
                                                component={CustomTextField}
                                                label="Número do processo SEI"
                                                name="nuProcessoSei"
                                                disabled={!canEdit}
                                                inputProps={{
                                                    maxLength: 30,
                                                }}
                                            />
                                        </Grid>
                                        {!loadingServiceFilter && (
                                            <RequestProcess
                                                {...props}
                                                {...formProps}
                                                initialState={formInitials}
                                                canEdit={canEdit}
                                                cdLinha={cdLinhaFromPath}
                                                servicesFilter={servicesFilter}
                                                requestProcessValues={requestProcessValues}
                                                setRequestProcessValues={setRequestProcessValues}
                                                callBackRequest={callBackRequest}
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            component={CustomTextField}
                                            label="Código da revisão"
                                            name="cdRevisao"
                                            fullWidth
                                            disabled
                                            loading={loading}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data de cadastro"
                                            name="dtCadastro"
                                            disabled={!canEdit}
                                            value={values.dtCadastro || ''}
                                            invalidLabel=""
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data de vigência"
                                            name="dtVigencia"
                                            disabled={!canEdit}
                                            value={values.dtVigencia || ''}
                                            invalidLabel=""
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            component={CustomTextField}
                                            label="Situação"
                                            name="cdLinhaSituacao"
                                            select
                                            loading={loading}
                                            disabled={loading || !canEdit}
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            <option value="">Selecione...</option>
                                            {situationsStore
                                                ?.filter((entry) => {
                                                    return !(
                                                        String(values.cdLinhaSituacao) !==
                                                            String(LINES_SITUATIONS.VIGENTE.code) &&
                                                        entry.cdLinhaSituacao === LINES_SITUATIONS.VIGENTE.code
                                                    );
                                                })
                                                .map((type, index) => (
                                                    <option key={`type-${index}`} value={type.cdLinhaSituacao}>
                                                        {type.deLinhaSituacao}
                                                    </option>
                                                ))}
                                        </Field>
                                    </Grid>

                                    {!isStrip && (
                                        <Grid item xs={12}>
                                            <PermissionariesTable
                                                {...props}
                                                data={permissionaries}
                                                setData={setPermissionaries}
                                                loading={loading}
                                                isInternalForm={true}
                                                disabled={!canEdit}
                                                setChanged={setChangedPermissionaries}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            component={CustomTextField}
                                            label="Nº Apostila do Q.H."
                                            name="nuApostilaFmt"
                                            disabled
                                        />
                                    </Grid>
                                    {isStrip && (
                                        <Grid item xs={12} sm={3}>
                                            <Field
                                                component={CustomTextField}
                                                label="Tipo"
                                                name="cdTipoQuadroHorario"
                                                select
                                                loading={loading}
                                                disabled={loading || !canEdit}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    setSelectedScheduleTypes(e.target.value);
                                                }}
                                                SelectProps={{
                                                    native: true,
                                                }}>
                                                <option value="">Selecione...</option>
                                                {scheduleTypes?.map((type, index) => (
                                                    <option key={`schetype-${index}`} value={type.cdTipoQuadroHorario}>
                                                        {type.deTipoQuadroHorario}
                                                    </option>
                                                ))}
                                            </Field>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} container alignItems="flex-start">
                                        {loadingSchedules && (
                                            <Grid item xs={12} className={classes.centerProgress}>
                                                <CircularProgress size={12} />
                                            </Grid>
                                        )}
                                        {!loadingSchedules && (
                                            <ScheduleTableBoard
                                                {...props}
                                                readOnly={!canEdit}
                                                selectedScheduleTypes={selectedScheduleTypes}
                                                permissionaries={permissionaries}
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            component={CustomTextField}
                                            name="deObservacao"
                                            label="Observação"
                                            multiline
                                            minRows={4}
                                            disabled={!canEdit}
                                            inputProps={{
                                                maxLength: 2000,
                                            }}
                                        />
                                    </Grid>

                                    {isStrip && canEdit && (
                                        <Grid item xs={12} container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Linha/Serviço a copiar Q.H"
                                                    select
                                                    variant="filled"
                                                    fullWidth
                                                    value={lineToCopy}
                                                    margin={'dense'}
                                                    onChange={(e) => {
                                                        setLineToCopy(e.target.value);
                                                    }}
                                                    SelectProps={{
                                                        native: true,
                                                    }}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}>
                                                    <option value="">Selecione...</option>
                                                    {allLinesToCopy.map((line, index) => {
                                                        return (
                                                            <option key={`line-${index}`} value={line.cdLinha}>
                                                                {line.deDescricao}
                                                            </option>
                                                        );
                                                    })}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                                                <Button
                                                    onClick={() => {
                                                        copySchedules(formProps);
                                                    }}
                                                    disableElevation
                                                    color={'primary'}
                                                    variant="contained"
                                                    className={classes.copyButton}>
                                                    Copiar
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        onCancel={handleCancel}
                                        onSave={() => {
                                            validateFormikForm(formProps, scheduleValidationSchema).then((res) => {
                                                if (Object.keys(res).length > 0) {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                } else {
                                                    handleSubmit(values);
                                                }
                                            });
                                        }}
                                        firstButtonLabel={'VOLTAR'}
                                        firstButtonDisabled={formProps.status.holding}
                                        middleButtonLabel={(() => {
                                            if (formProps.status.holding) {
                                                return 'Aguarde...';
                                            }
                                            if (canEdit) {
                                                return 'SALVAR';
                                            }
                                            return null;
                                        })()}
                                        middleButtonDisabled={formProps.status.holding}
                                        lastButtonDisabled={
                                            formProps.status.holding ||
                                            !canGenerateScheduleBoard ||
                                            !values.cdLinhaQuadroHorario ||
                                            changedPermissionaries
                                        }
                                        lastButtonLabel={'EMISSÃO Q.H.'}
                                        onClickLastButton={() => handleGenerateQH({ values, formProps })}
                                    />
                                </Grid>
                            </PageWrapper>
                        </Form>
                    );
                }}
            </Formik>

            <ScheduleModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                data={modalData}
                updateSchedule={updateSchedule}
            />
        </FormServiceContext.Provider>
    );
};

const useStyles = makeStyles(styles);

export default SchedulesDetails;
